import React, { useState } from 'react'
import {
  Heading,
  Text,
  Button,
  TextInput,
  Textarea,
  Select,
  FormControl,
  InlineLink
} from '@primer/react-brand'
import ChromelessAuthLayout from '../components/ChromelessAuthLayout'
import { PERSONAL_EMAIL_DOMAINS, ORG_TYPES } from '../../components/subscriptionConstants'
import { validateRequired } from '../utilities/validateAuthForm'

// PASSWORDLESS SINCE 2026-09-13 (Cameron): email → name → organization, then a
// verification link. Submitting creates the account but starts nothing; the link
// starts the trial. See
// docs/superpowers/specs/2026-09-13-civic-lens-passwordless-access-design.md.
//
// `skipEmailCheck` comes from /sign-up?skip_email_check=true — an unsecured test
// door that skips the personal-domain and existing-domain rules only.
const SignUpPage = ({ signInPath = '/sign-in', skipEmailCheck = false }) => {
  const [step, setStep] = useState('email')
  const [email, setEmail] = useState('')
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)

  const [subStep, setSubStep] = useState('profile')
  const [fullName, setFullName] = useState('')
  const [profileError, setProfileError] = useState(null)

  // 'check_email' once the verification link is on its way.
  const [resendState, setResendState] = useState(null)

  const [orgFields, setOrgFields] = useState({
    name: '', address: '', url: '', organization_type: '', describe_work: '', reason: ''
  })
  const [orgErrors, setOrgErrors] = useState({})
  const [orgLoading, setOrgLoading] = useState(false)

  // Domain-collision request-to-join (UC-12). `orgExists` ({ domain, url }) routes
  // to the "This organization already exists" screen — set from either the email
  // step (url null) or the org-URL step (url set, so the server can resolve the
  // colliding org). `requestSent` ({ orgName }) swaps in the confirmation screen.
  const [orgExists, setOrgExists] = useState(null)
  const [requestSent, setRequestSent] = useState(null)
  const [requestLoading, setRequestLoading] = useState(false)
  const [requestError, setRequestError] = useState(null)

  // Email step
  const handleEmailChange = (e) => {
    setEmail(e.target.value)
    if (error) setError(null)
  }

  const handleEmailSubmit = async (e) => {
    e.preventDefault()
    const trimmedEmail = email.trim()

    const requiredError = validateRequired([
      { value: trimmedEmail, message: 'Work email is required.' }
    ])
    if (requiredError) { setError(requiredError); return }

    const domain = trimmedEmail.split('@')[1]?.toLowerCase()
    if (!skipEmailCheck && domain && PERSONAL_EMAIL_DOMAINS.includes(domain)) {
      setError({ type: 'personal_domain', message: "Please use your work email. If you are unable to apply this way, please reach out to us at access@civiclens.dev." })
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await fetch('/sign-up/check_email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        },
        credentials: 'same-origin',
        body: JSON.stringify({ email: trimmedEmail, skip_email_check: skipEmailCheck })
      })
      const data = await response.json()
      if (data.valid) {
        setStep('profile')
      } else if (data.reason === 'pending_verification') {
        // Signed up before and never used the link: send it again.
        await resendLink(trimmedEmail)
        setStep('check_email')
      } else if (data.reason === 'personal_domain') {
        setError({ type: 'personal_domain', message: "Please use your work email. If you are unable to apply this way, please reach out to us at access@civiclens.dev." })
      } else if (data.reason === 'already_registered') {
        setError({ type: 'already_registered' })
      } else if (data.reason === 'org_exists') {
        // Route to the request-to-join screen instead of an inline error (UC-12).
        setOrgExists({ domain, name: data.organization?.name, url: null })
      }
    } catch (err) {
      setError({ type: 'general', message: 'Something went wrong. Please try again.' })
    } finally {
      setLoading(false)
    }
  }

  // Re-sends the verification link. The sign-in-link endpoint sends an
  // unverified sign-up its verification link, so one endpoint serves both.
  const resendLink = async (address) => {
    setResendState('sending')
    try {
      const response = await fetch('/api/v1/auth/sign_in_link', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        },
        credentials: 'same-origin',
        body: JSON.stringify({ email: address })
      })
      const data = await response.json()
      setResendState(data.success ? 'sent' : 'failed')
    } catch (err) {
      setResendState('failed')
    }
  }

  // Profile sub-step
  const handleProfileSubmit = (e) => {
    e.preventDefault()
    const nameError = validateRequired([{ value: fullName, message: 'Full name is required.' }])
    if (nameError) { setProfileError({ ...nameError, field: 'fullName' }); return }
    if (fullName.trim().split(/\s+/).length < 2) {
      setProfileError({ type: 'required', message: 'Please enter your first and last name.', field: 'fullName' })
      return
    }
    setProfileError(null)
    setSubStep('org')
  }

  const handleNotYou = () => {
    setStep('email')
    setSubStep('profile')
    setEmail('')
    setFullName('')
    setProfileError(null)
    setOrgFields({ name: '', address: '', url: '', organization_type: '', describe_work: '', reason: '' })
    setOrgErrors({})
  }

  // Org sub-step
  const handleOrgChange = (field) => (e) => {
    const value = e.target.value
    setOrgFields(prev => ({ ...prev, [field]: value }))
    if (orgErrors[field]) setOrgErrors(prev => { const next = { ...prev }; delete next[field]; return next })
  }

  const handleOrgSubmit = async (e) => {
    e.preventDefault()

    const newErrors = {}
    if (!orgFields.name.trim()) newErrors.name = 'Organization name is required.'
    if (!orgFields.address.trim()) newErrors.address = 'Address is required.'
    if (!orgFields.url.trim()) {
      newErrors.url = 'Organization domain or URL is required.'
    } else if (!/^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+/i.test(orgFields.url.trim())) {
      newErrors.url = 'Please enter a valid domain or URL.'
    }
    if (!orgFields.organization_type) newErrors.organization_type = 'Please select an organization type.'
    if (!orgFields.describe_work.trim()) newErrors.describe_work = 'Please describe your work.'
    if (!orgFields.reason.trim()) newErrors.reason = 'Please explain how you plan to use CivicLens.'

    if (Object.keys(newErrors).length > 0) {
      setOrgErrors(newErrors)
      return
    }

    setOrgLoading(true)
    setOrgErrors({})

    try {
      const nameParts = fullName.trim().split(/\s+/)
      const firstName = nameParts[0] || ''
      const lastName = nameParts.slice(1).join(' ') || ''

      const response = await fetch('/api/v1/auth/sign_up', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          email,
          first_name: firstName,
          last_name: lastName,
          organization: { ...orgFields },
          skip_email_check: skipEmailCheck
        })
      })

      const data = await response.json()

      if (data.success) {
        setStep('check_email')
      } else if (data.reason === 'org_exists') {
        // The entered org URL collides with an existing org — route to the
        // request-to-join screen (UC-12), same as the email-step collision.
        setOrgExists({ domain: data.domain, name: data.organization?.name, url: orgFields.url.trim() })
      } else if (data.errors) {
        const mapped = {}
        Object.entries(data.errors).forEach(([key, messages]) => {
          const msg = Array.isArray(messages) ? messages[0] : messages
          if (key.startsWith('organization.')) {
            mapped[key.replace('organization.', '')] = msg
          } else if (key === 'url' || key === 'general') {
            mapped[key] = msg
          } else {
            mapped.general = mapped.general || msg
          }
        })
        setOrgErrors(mapped)
      } else {
        setOrgErrors({ general: 'Something went wrong. Please try again.' })
      }
    } catch (err) {
      setOrgErrors({ general: 'Something went wrong. Please try again.' })
    } finally {
      setOrgLoading(false)
    }
  }

  // Request-to-join: notify the existing org's admins (UC-12).
  const handleRequestAccess = async () => {
    setRequestLoading(true)
    setRequestError(null)

    try {
      const response = await fetch('/sign-up/request-access', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        },
        credentials: 'same-origin',
        body: JSON.stringify({ email, url: orgExists?.url || undefined })
      })
      const data = await response.json()
      if (data.success) {
        setRequestSent({ orgName: data.organization?.name })
      } else {
        setRequestError('Something went wrong. Please try again.')
      }
    } catch (err) {
      setRequestError('Something went wrong. Please try again.')
    } finally {
      setRequestLoading(false)
    }
  }

  // The verification link is on its way. Nothing has started yet: the link
  // verifies the address and starts the trial.
  if (step === 'check_email') {
    return (
      <ChromelessAuthLayout>
        <Heading as="h1" size="5">Check your email</Heading>
        <Text size="200" variant="muted" as="div" style={{ marginTop: '14px' }}>
          We've sent a link to <strong style={{ color: 'var(--base-color-scale-gray-9)' }}>{email}</strong>. Open it to verify your email and start your free trial.
        </Text>
        <Text size="200" variant="muted" as="div" style={{ marginTop: '22px' }}>
          {resendState === 'sent' ? (
            "We've sent it again."
          ) : resendState === 'failed' ? (
            'Something went wrong. Please try again.'
          ) : (
            <>
              Didn't get it?{' '}
              <InlineLink onClick={() => resendLink(email)} style={{ cursor: 'pointer' }}>
                {resendState === 'sending' ? 'Sending...' : 'Send it again'}
              </InlineLink>
            </>
          )}
        </Text>
      </ChromelessAuthLayout>
    )
  }

  // Confirmation screen after the join request is sent (UC-12). Mirrors the
  // ForgotPasswordPage "submitted" pattern.
  if (requestSent) {
    return (
      <ChromelessAuthLayout>
        <div
          style={{
            width: '52px',
            height: '52px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '22px',
            marginLeft: 'auto',
            marginRight: 'auto',
            backgroundColor: 'var(--base-color-scale-blue-0)'
          }}
        >
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M20 6.5 9.5 17 4.5 12" stroke="var(--base-color-scale-blue-5)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <Heading as="h1" size="5" style={{ textAlign: 'center' }}>Request sent</Heading>
        <Text size="200" variant="muted" as="div" style={{ marginTop: '14px' }}>
          We've let the administrators of <strong style={{ color: 'var(--base-color-scale-gray-9)' }}>{requestSent.orgName || 'the organization'}</strong> know you'd like to join. They'll add you to the organization.
        </Text>
        <Text size="200" variant="muted" as="div" style={{ marginTop: '22px' }}>
          <InlineLink href={signInPath}>Return to sign in</InlineLink>
        </Text>
      </ChromelessAuthLayout>
    )
  }

  // "This organization already exists" request-to-join screen (UC-12). Design:
  // Prototype C_A_primer_v2.html OrgExistsScreen. Prototype hex mapped to tokens
  // per docs/design-tokens.md (Civic Lens react-brand table).
  if (orgExists) {
    const requesterLabel = email || `you@${orgExists.domain}`
    const steps = [
      "The organization's administrators are notified of your request.",
      'An administrator reviews and approves you.',
      "You'll get an email and can sign in to CivicLens."
    ]
    return (
      <ChromelessAuthLayout maxWidth={420}>
        <Heading as="h1" size="5">This organization already exists</Heading>
        <Text size="200" variant="muted" as="div" style={{ marginTop: '12px' }}>
          <strong style={{ color: 'var(--base-color-scale-gray-9)' }}>{orgExists.name || orgExists.domain}</strong> is already registered with CivicLens.
        </Text>

        <div style={{ marginTop: '20px', border: '1px solid var(--base-color-scale-gray-1)', borderRadius: '8px', padding: '14px 16px' }}>
          <Text size="200" as="div" style={{ marginBottom: '10px' }}>
            <strong style={{ color: 'var(--base-color-scale-gray-9)' }}>What happens when you request access</strong>
          </Text>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
            {steps.map((stepText, i) => (
              <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                <span
                  style={{
                    flex: 'none',
                    width: '19px',
                    height: '19px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--base-color-scale-gray-1)',
                    color: 'var(--base-color-scale-gray-6)',
                    fontSize: '11px',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: '1px'
                  }}
                >
                  {i + 1}
                </span>
                <Text size="200" variant="muted" as="span">{stepText}</Text>
              </div>
            ))}
          </div>
        </div>

        <Button
          type="button"
          variant="primary"
          block={true}
          disabled={requestLoading}
          onClick={handleRequestAccess}
          style={{
            marginTop: '20px',
            backgroundColor: 'var(--base-color-scale-blue-5)',
            borderColor: 'var(--base-color-scale-blue-5)',
            borderRadius: '6px'
          }}
        >
          {requestLoading ? 'Sending request...' : 'Request access'}
        </Button>

        {requestError && (
          <p style={{ marginTop: '12px', color: 'var(--brand-color-error-fg)', fontSize: '14px' }}>
            {requestError}
          </p>
        )}

        <Text size="100" variant="muted" as="div" style={{ marginTop: '16px' }}>
          Requesting as <strong style={{ color: 'var(--base-color-scale-gray-9)' }}>{requesterLabel}</strong>.
        </Text>
      </ChromelessAuthLayout>
    )
  }

  // Sub-step A: "Create your profile"
  if (step === 'profile' && subStep === 'profile') {
    return (
      <ChromelessAuthLayout>
        <Heading as="h1" size="5">Create your profile</Heading>

        <form onSubmit={handleProfileSubmit} style={{ marginTop: '24px' }}>
          <FormControl fullWidth validationStatus={profileError?.field === 'fullName' ? 'error' : undefined}>
            <FormControl.Label>Full name</FormControl.Label>
            <TextInput
              name="fullName"
              type="text"
              value={fullName}
              onChange={(e) => { setFullName(e.target.value); if (profileError?.field === 'fullName') setProfileError(null) }}
              placeholder="First and last"
            />
            {profileError?.field === 'fullName' && (
              <FormControl.Validation validationStatus="error">{profileError.message}</FormControl.Validation>
            )}
          </FormControl>

          <Button
            type="submit"
            variant="primary"
            block={true}
            style={{
              marginTop: '22px',
              backgroundColor: 'var(--base-color-scale-blue-5)',
              borderColor: 'var(--base-color-scale-blue-5)',
              borderRadius: '6px'
            }}
          >
            Sign up
          </Button>

          <Text size="100" variant="muted" as="div" style={{ marginTop: '28px' }}>
            Signing up as <strong>{email}</strong>.{' '}
            <InlineLink onClick={handleNotYou} style={{ cursor: 'pointer' }}>Not you?</InlineLink>
          </Text>
        </form>
      </ChromelessAuthLayout>
    )
  }

  // Sub-step B: "Tell us about your organization"
  if (step === 'profile' && subStep === 'org') {
    return (
      <ChromelessAuthLayout maxWidth={480}>
        <Heading as="h1" size="5">Tell us about your organization</Heading>
        <Text as="p" size="300" variant="muted" style={{ marginTop: 6, marginBottom: 28, lineHeight: 1.65 }}>
          Because CivicLens is available only to organizations that use it in ways consistent with our mission to strengthen civic life in America, we need to understand a little more about your organization and how you plan to use it.
        </Text>

        <form onSubmit={handleOrgSubmit}>
          <FormControl fullWidth validationStatus={orgErrors.name ? 'error' : undefined}>
            <FormControl.Label>Organization name</FormControl.Label>
            <TextInput
              name="name"
              type="text"
              value={orgFields.name}
              onChange={handleOrgChange('name')}
              placeholder="e.g. Acme Newsroom"
            />
            {orgErrors.name && (
              <FormControl.Validation validationStatus="error">{orgErrors.name}</FormControl.Validation>
            )}
          </FormControl>

          <FormControl fullWidth validationStatus={orgErrors.address ? 'error' : undefined} style={{ marginTop: '14px' }}>
            <FormControl.Label>Address</FormControl.Label>
            <TextInput
              name="address"
              type="text"
              value={orgFields.address}
              onChange={handleOrgChange('address')}
              placeholder="Street, city, state"
            />
            {orgErrors.address && (
              <FormControl.Validation validationStatus="error">{orgErrors.address}</FormControl.Validation>
            )}
          </FormControl>

          <FormControl fullWidth validationStatus={orgErrors.url ? 'error' : undefined} style={{ marginTop: '14px' }}>
            <FormControl.Label>Organization domain or URL</FormControl.Label>
            <TextInput
              name="url"
              type="text"
              value={orgFields.url}
              onChange={handleOrgChange('url')}
              placeholder="e.g. acmenewsroom.org"
            />
            {orgErrors.url && (
              <FormControl.Validation validationStatus="error">{orgErrors.url}</FormControl.Validation>
            )}
          </FormControl>

          <FormControl fullWidth validationStatus={orgErrors.organization_type ? 'error' : undefined} style={{ marginTop: '18px' }}>
            <FormControl.Label>What kind of organization do you represent?</FormControl.Label>
            <Select
              fullWidth
              value={orgFields.organization_type}
              onChange={handleOrgChange('organization_type')}
            >
              <Select.Option value="" disabled>Choose one</Select.Option>
              {ORG_TYPES.map(type => (
                <Select.Option key={type} value={type}>{type}</Select.Option>
              ))}
            </Select>
            {orgErrors.organization_type && (
              <FormControl.Validation validationStatus="error">{orgErrors.organization_type}</FormControl.Validation>
            )}
          </FormControl>

          <FormControl fullWidth validationStatus={orgErrors.describe_work ? 'error' : undefined} style={{ marginTop: '18px' }}>
            <FormControl.Label>Describe your work</FormControl.Label>
            <Textarea
              fullWidth
              resize="vertical"
              rows={3}
              name="describe_work"
              value={orgFields.describe_work}
              onChange={handleOrgChange('describe_work')}
              placeholder="Please give us a brief overview of what your organization does."
            />
            {orgErrors.describe_work && (
              <FormControl.Validation validationStatus="error">{orgErrors.describe_work}</FormControl.Validation>
            )}
          </FormControl>

          <FormControl fullWidth validationStatus={orgErrors.reason ? 'error' : undefined} style={{ marginTop: '18px' }}>
            <FormControl.Label>How do you plan to use CivicLens?</FormControl.Label>
            <Textarea
              fullWidth
              resize="vertical"
              rows={3}
              name="reason"
              value={orgFields.reason}
              onChange={handleOrgChange('reason')}
              placeholder="Please explain how you would like to use CivicLens in your work."
            />
            {orgErrors.reason && (
              <FormControl.Validation validationStatus="error">{orgErrors.reason}</FormControl.Validation>
            )}
          </FormControl>

          {orgErrors.general && (
            <p style={{ marginTop: '12px', color: 'var(--brand-color-error-fg)', fontSize: '14px' }}>
              {orgErrors.general}
            </p>
          )}

          <Button
            type="submit"
            variant="primary"
            block={true}
            disabled={orgLoading}
            style={{
              marginTop: '24px',
              backgroundColor: 'var(--base-color-scale-blue-5)',
              borderColor: 'var(--base-color-scale-blue-5)',
              borderRadius: '6px'
            }}
          >
            {orgLoading ? 'Creating account...' : 'Create account'}
          </Button>
        </form>
      </ChromelessAuthLayout>
    )
  }

  // Step 1: Email entry
  return (
    <ChromelessAuthLayout>
      <Heading as="h1" size="5">Welcome to CivicLens</Heading>

      <form onSubmit={handleEmailSubmit} style={{ marginTop: '28px' }}>
        <FormControl fullWidth validationStatus={error ? 'error' : undefined}>
          <FormControl.Label>Work email</FormControl.Label>
          <TextInput
            name="email"
            type="email"
            value={email}
            onChange={handleEmailChange}
            placeholder="name@organisation.org"
          />
          {(error?.type === 'required' || error?.type === 'personal_domain' || error?.type === 'general') && (
            <FormControl.Validation validationStatus="error">{error.message}</FormControl.Validation>
          )}
          {error?.type === 'already_registered' && (
            <FormControl.Validation validationStatus="error">
              It looks like you already have an account.{' '}
              <InlineLink href={`${signInPath}?email=${encodeURIComponent(email.trim())}`}>Sign in</InlineLink>
            </FormControl.Validation>
          )}
        </FormControl>

        <Button
          type="submit"
          variant="primary"
          block={true}
          disabled={loading}
          style={{
            marginTop: '14px',
            backgroundColor: 'var(--base-color-scale-blue-5)',
            borderColor: 'var(--base-color-scale-blue-5)',
            borderRadius: '6px'
          }}
        >
          {loading ? 'Checking...' : 'Continue with email'}
        </Button>

        <Text size="100" variant="muted" as="div" style={{ marginTop: '18px' }}>
          By creating an account, you agree to the{' '}
          <InlineLink href="/terms">Terms of Service</InlineLink> and{' '}
          <InlineLink href="/privacy">Privacy Policy</InlineLink>.
        </Text>

        <Text size="200" variant="muted" as="div" style={{ marginTop: '24px' }}>
          Already have an account?{' '}
          <InlineLink href={signInPath}>Sign in</InlineLink>
        </Text>
      </form>
    </ChromelessAuthLayout>
  )
}

export default SignUpPage
